import {
  type ChakraStylesConfig,
  chakraComponents,
  type GroupBase,
  type MultiValueRemoveProps,
  type Props,
  Select,
  type SelectComponentsConfig,
  type SelectInstance,
} from 'chakra-react-select';
import type { ForwardedRef, ReactElement, RefAttributes } from 'react';
import { forwardRef, useMemo } from 'react';

import { THEMED_SELECT_CLASS_NAME_PREFIX } from '../themed-select/themedSelectClassNamePrefix';
import { createEntitySearchSelectChakraStyles } from './entitySearchSelectStyles';
import './entitySearchSelectTheme.module.css';

type EntitySearchSelectBaseProps = {
  /**
   * LandingSearch has a left-side entity dropdown button (a "left addon"),
   * so the select control has its left border-radius removed to visually connect.
   */
  hasLeftAddon?: boolean;
  /**
   * When set (e.g. `1`), the chip remove "x" will be hidden when the number of
   * selected values is <= this number.
   *
   * This is useful for defaults that must never be empty.
   */
  minSelectedValues?: number;
};

type EntitySearchSelectChakraProps = {
  colorScheme?: string;
  tagColorScheme?: string;
  size?: string;
};

export type EntitySearchSelectProps<TOption> = EntitySearchSelectBaseProps &
  Omit<
    Props<TOption, true, GroupBase<TOption>>,
    'isMulti' | 'components' | 'chakraStyles'
  > &
  EntitySearchSelectChakraProps & {
    components?: SelectComponentsConfig<TOption, true, GroupBase<TOption>>;
    chakraStyles?: ChakraStylesConfig<TOption, true, GroupBase<TOption>>;
  };

const baseComponents = {
  DropdownIndicator: () => null,
  IndicatorSeparator: () => null,
} as const;

const createMinSelectedValuesRemove = <TOption,>({
  minSelectedValues,
  baseRemoveComponent,
}: {
  minSelectedValues: number;
  baseRemoveComponent:
    | SelectComponentsConfig<
        TOption,
        true,
        GroupBase<TOption>
      >['MultiValueRemove']
    | undefined;
}) => {
  const RemoveComponent =
    baseRemoveComponent ?? chakraComponents.MultiValueRemove;

  return (props: MultiValueRemoveProps<TOption, true, GroupBase<TOption>>) => {
    const selectedOptions = props.selectProps.value as TOption[];
    if (selectedOptions.length <= minSelectedValues) {
      return null;
    }

    return <RemoveComponent {...props} />;
  };
};

type EntitySearchSelectComponent = <TOption = unknown>(
  props: EntitySearchSelectProps<TOption> &
    RefAttributes<SelectInstance<TOption, true, GroupBase<TOption>>>
) => ReactElement;

function EntitySearchSelectInner<TOption>(
  {
    hasLeftAddon = false,
    minSelectedValues,
    chakraStyles,
    components,
    colorScheme,
    tagColorScheme,
    size = 'md',
    closeMenuOnSelect = false,
    isClearable = false,
    isSearchable = true,
    ...props
  }: EntitySearchSelectProps<TOption>,
  ref: ForwardedRef<SelectInstance<TOption, true, GroupBase<TOption>>>
) {
  const baseStyles = useMemo(
    () => createEntitySearchSelectChakraStyles<TOption>({ hasLeftAddon }),
    [hasLeftAddon]
  );

  const mergedChakraStyles = useMemo(() => {
    return {
      ...baseStyles,
      ...(chakraStyles ?? {}),
    };
  }, [baseStyles, chakraStyles]);

  const mergedComponents = useMemo(() => {
    const minRemove =
      typeof minSelectedValues === 'number' && minSelectedValues > 0
        ? createMinSelectedValuesRemove<TOption>({
            minSelectedValues,
            baseRemoveComponent: components?.MultiValueRemove,
          })
        : undefined;

    return {
      ...baseComponents,
      ...(components ?? {}),
      ...(minRemove ? { MultiValueRemove: minRemove } : {}),
    };
  }, [components, minSelectedValues]);

  const resolvedTagColorScheme = tagColorScheme ?? colorScheme ?? 'green';

  return (
    <Select<TOption, true, GroupBase<TOption>>
      classNamePrefix={THEMED_SELECT_CLASS_NAME_PREFIX}
      {...props}
      ref={ref}
      isMulti
      tagColorScheme={resolvedTagColorScheme}
      size={size}
      closeMenuOnSelect={closeMenuOnSelect}
      isClearable={isClearable}
      isSearchable={isSearchable}
      chakraStyles={mergedChakraStyles}
      components={mergedComponents}
    />
  );
}

export const EntitySearchSelect: EntitySearchSelectComponent = forwardRef(
  EntitySearchSelectInner
) as unknown as EntitySearchSelectComponent;
